import { Injectable } from '@angular/core';
import { DoseType, Product } from '../interfaces/product.interface';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProductService } from './product.service';

export interface DoseColorTheme {
  primary: string;
  secondary: string;
  light: string;
  dark: string;
  gradient: string;
  textOnPrimary: string;
  textOnLight: string;
}

@Injectable({
  providedIn: 'root'
})
export class DoseColorService {

  // Keep legacy mappings for backward compatibility
  private doseColorMap: Record<string, DoseColorTheme> = {
    green: {
      primary: '#03441F',
      secondary: '#046B34',
      light: '#E8F3ED',
      dark: '#012B14',
      gradient: 'linear-gradient(135deg, #046B34 0%, #03441F 50%, #012B14 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#03441F'
    },
    yellow: {
      primary: '#FF9800',
      secondary: '#FFB74D',
      light: '#FFF9C4',
      dark: '#E65100',
      gradient: 'linear-gradient(135deg, #FFB74D 0%, #FF9800 50%, #F57C00 100%)',
      textOnPrimary: '#1a1a1a',
      textOnLight: '#E65100'
    },
    red: {
      primary: '#B71C1C',
      secondary: '#D32F2F',
      light: '#FFEBEE',
      dark: '#8B0000',
      gradient: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 50%, #8B0000 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#B71C1C'
    },
    // Extended preset colors for common dose types
    blue: {
      primary: '#2196F3',
      secondary: '#42A5F5',
      light: '#E3F2FD',
      dark: '#1565C0',
      gradient: 'linear-gradient(135deg, #42A5F5 0%, #2196F3 50%, #1976D2 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#1565C0'
    },
    purple: {
      primary: '#9C27B0',
      secondary: '#BA68C8',
      light: '#F3E5F5',
      dark: '#7B1FA2',
      gradient: 'linear-gradient(135deg, #BA68C8 0%, #9C27B0 50%, #7B1FA2 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#7B1FA2'
    },
    orange: {
      primary: '#FF9800',
      secondary: '#FFB74D',
      light: '#FFF3E0',
      dark: '#F57C00',
      gradient: 'linear-gradient(135deg, #FFB74D 0%, #FF9800 50%, #F57C00 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#F57C00'
    },
    pink: {
      primary: '#E91E63',
      secondary: '#F48FB1',
      light: '#FCE4EC',
      dark: '#C2185B',
      gradient: 'linear-gradient(135deg, #F48FB1 0%, #E91E63 50%, #C2185B 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#C2185B'
    },
    teal: {
      primary: '#009688',
      secondary: '#4DB6AC',
      light: '#E0F2F1',
      dark: '#00796B',
      gradient: 'linear-gradient(135deg, #4DB6AC 0%, #009688 50%, #00796B 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#00796B'
    },
    indigo: {
      primary: '#3F51B5',
      secondary: '#7986CB',
      light: '#E8EAF6',
      dark: '#303F9F',
      gradient: 'linear-gradient(135deg, #7986CB 0%, #3F51B5 50%, #303F9F 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#303F9F'
    },
    cyan: {
      primary: '#00BCD4',
      secondary: '#4DD0E1',
      light: '#E0F7FA',
      dark: '#0097A7',
      gradient: 'linear-gradient(135deg, #4DD0E1 0%, #00BCD4 50%, #0097A7 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#0097A7'
    },
    lime: {
      primary: '#CDDC39',
      secondary: '#D4E157',
      light: '#F9FBE7',
      dark: '#AFB42B',
      gradient: 'linear-gradient(135deg, #D4E157 0%, #CDDC39 50%, #AFB42B 100%)',
      textOnPrimary: '#000000',
      textOnLight: '#AFB42B'
    },
    amber: {
      primary: '#FFC107',
      secondary: '#FFD54F',
      light: '#FFF8E1',
      dark: '#FF8F00',
      gradient: 'linear-gradient(135deg, #FFD54F 0%, #FFC107 50%, #FF8F00 100%)',
      textOnPrimary: '#000000',
      textOnLight: '#FF8F00'
    },
    brown: {
      primary: '#795548',
      secondary: '#A1887F',
      light: '#EFEBE9',
      dark: '#5D4037',
      gradient: 'linear-gradient(135deg, #A1887F 0%, #795548 50%, #5D4037 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#5D4037'
    },
    gray: {
      primary: '#9E9E9E',
      secondary: '#BDBDBD',
      light: '#FAFAFA',
      dark: '#616161',
      gradient: 'linear-gradient(135deg, #BDBDBD 0%, #9E9E9E 50%, #757575 100%)',
      textOnPrimary: '#FFFFFF',
      textOnLight: '#616161'
    }
  };

  constructor(private productService: ProductService) { }

  /**
   * Get the complete color theme for a dose type
   */
  getDoseColorTheme(doseType: DoseType): DoseColorTheme {
    if (this.doseColorMap[doseType]) {
      return this.doseColorMap[doseType];
    }
    return this.generateDynamicTheme(doseType);
  }

  /**
   * Generate dynamic color theme for new dose types
   */
  private generateDynamicTheme(doseType: string): DoseColorTheme {
    const hash = this.hashString(doseType);
    const hue = hash % 360;

    // Create a more sophisticated color palette
    const saturation = 65 + (hash % 25); // 65-90% for vibrant colors
    const lightness = 40 + (hash % 20); // 40-60% for good contrast

    // Generate complementary and analogous colors for better harmony
    const primary = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    const secondary = `hsl(${hue}, ${saturation - 10}%, ${lightness + 5}%)`;
    const light = `hsl(${hue}, ${saturation - 20}%, ${lightness + 35}%)`;
    const dark = `hsl(${hue}, ${saturation + 10}%, ${lightness - 20}%)`;

    // Create multiple gradient options for variety
    const gradients = [
      `linear-gradient(135deg, ${secondary} 0%, ${primary} 50%, ${dark} 100%)`,
      `linear-gradient(45deg, ${light} 0%, ${primary} 50%, ${dark} 100%)`,
      `linear-gradient(225deg, ${secondary} 0%, ${primary} 50%, ${light} 100%)`,
      `linear-gradient(315deg, ${dark} 0%, ${primary} 50%, ${secondary} 100%)`
    ];

    // Select gradient based on hash for variety
    const selectedGradient = gradients[hash % gradients.length];

    return {
      primary,
      secondary,
      light,
      dark,
      gradient: selectedGradient,
      textOnPrimary: this.getContrastColor(primary),
      textOnLight: this.getContrastColor(light)
    };
  }

  /**
   * Get the primary color for a dose type
   */
  getDosePrimaryColor(doseType: DoseType): string {
    return this.getDoseColorTheme(doseType).primary;
  }

  /**
   * Get the light background color for a dose type
   */
  getDoseLightColor(doseType: DoseType): string {
    return this.getDoseColorTheme(doseType).light;
  }

  /**
   * Get the gradient for a dose type
   */
  getDoseGradient(doseType: DoseType): string {
    return this.getDoseColorTheme(doseType).gradient;
  }

  /**
   * Get text color that works well on the primary color
   */
  getTextOnPrimary(doseType: DoseType): string {
    return this.getDoseColorTheme(doseType).textOnPrimary;
  }

  /**
   * Get text color that works well on the light background
   */
  getTextOnLight(doseType: DoseType): string {
    return this.getDoseColorTheme(doseType).textOnLight;
  }

  /**
   * Generate CSS custom properties for a dose theme
   */
  getDoseCSSProperties(doseType: DoseType): Record<string, string> {
    const theme = this.getDoseColorTheme(doseType);
    return {
      '--dose-primary': theme.primary,
      '--dose-secondary': theme.secondary,
      '--dose-light': theme.light,
      '--dose-dark': theme.dark,
      '--dose-gradient': theme.gradient,
      '--dose-text-on-primary': theme.textOnPrimary,
      '--dose-text-on-light': theme.textOnLight
    };
  }

  /**
   * Generate CSS variables string for inline styles
   */
  getDoseCSSVariables(doseType: DoseType): string {
    const properties = this.getDoseCSSProperties(doseType);
    return Object.entries(properties)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
  }

  /**
   * Get a random preset dose type (useful for testing or fallbacks)
   */
  getRandomPresetDoseType(): string {
    const presetTypes = this.getPresetDoseTypes();
    const randomIndex = Math.floor(Math.random() * presetTypes.length);
    return presetTypes[randomIndex];
  }

  /**
   * Get all preset dose types (for reference and validation)
   */
  getPresetDoseTypes(): string[] {
    return Object.keys(this.doseColorMap);
  }

  /**
   * Check if a dose type has preset colors
   */
  isPresetDoseType(doseType: DoseType): boolean {
    return doseType in this.doseColorMap;
  }

  /**
   * Get the dose display name from products
   */
  getDoseDisplayName(doseType: DoseType): Observable<string> {
    return this.productService.getProducts().pipe(
      map((products: Product[]) => {
        const product = products.find(p => p.doseType === doseType);
        if (product) {
          return product.name;
        }
        // Fallback to static names for legacy types
        const names: { [key: string]: string } = {
          green: 'Dose One',
          yellow: 'Dose Two',
          red: 'Dose Three'
        };
        return names[doseType] || `Dose ${doseType}`;
      }),
      catchError(error => {
        console.error('Error fetching dose display name:', error);
        // Fallback to static names on error
        const names: { [key: string]: string } = {
          green: 'Dose One',
          yellow: 'Dose Two',
          red: 'Dose Three'
        };
        return of(names[doseType] || `Dose ${doseType}`);
      })
    );
  }

  /**
   * Get the dose number from products
   */
  getDoseNumber(doseType: DoseType): Observable<number> {
    return this.productService.getProducts().pipe(
      map((products: Product[]) => {
        const product = products.find(p => p.doseType === doseType);
        if (product) {
          return product.doseNumber || 0;
        }
        // Fallback to static mapping for legacy types
        const numbers: { [key: string]: number } = { green: 1, yellow: 2, red: 3 };
        return numbers[doseType] || 0;
      }),
      catchError(error => {
        console.error('Error fetching dose number:', error);
        // Fallback to static mapping on error
        const numbers: { [key: string]: number } = { green: 1, yellow: 2, red: 3 };
        return of(numbers[doseType] || 0);
      })
    );
  }

  /**
   * Get dose type from number using products
   */
  getDoseTypeFromNumber(doseNumber: number): Observable<DoseType> {
    return this.productService.getProducts().pipe(
      map((products: Product[]) => {
        const product = products.find(p => p.doseNumber === doseNumber);
        if (product) {
          return product.doseType;
        }
        // Fallback to static mapping for legacy types
        const types: Record<number, DoseType> = { 1: 'green', 2: 'yellow', 3: 'red' };
        return types[doseNumber] || 'green';
      }),
      catchError(error => {
        console.error('Error fetching dose type:', error);
        // Fallback to static mapping on error
        const types: Record<number, DoseType> = { 1: 'green', 2: 'yellow', 3: 'red' };
        return of(types[doseNumber] || 'green');
      })
    );
  }

  /**
   * Get all available dose types from products
   */
  getAllDoseTypes(): Observable<DoseType[]> {
    return this.productService.getProducts().pipe(
      map((products: Product[]) => {
        const doseTypes = products
          .map(p => p.doseType)
          .filter((type, index, self) => self.indexOf(type) === index) // Remove duplicates
          .sort((a, b) => {
            // Sort by dose number instead of hardcoded order
            const productA = products.find(p => p.doseType === a);
            const productB = products.find(p => p.doseType === b);
            return (productA?.doseNumber || 0) - (productB?.doseNumber || 0);
          });
        return doseTypes;
      }),
      catchError(error => {
        console.error('Error fetching all dose types:', error);
        // Return empty array instead of hardcoded fallback
        return of([]);
      })
    );
  }

  /**
   * Generate inline styles for dose theming
   */
  generateDoseForCountStyles(doseType: DoseType,isCurrunt : boolean): Record<string, string> {
    const theme = this.getDoseColorTheme(doseType);
    return {
      'background-color': isCurrunt ? theme.primary :  theme.light,
      'border-color': theme.light,
      'color': isCurrunt ? theme.textOnPrimary : '#ffffff',
    };
  }

  /**
   * Generate gradient background style
   */
  generateGradientStyle(doseType: DoseType): Record<string, string> {
    return {
      'background': this.getDoseGradient(doseType),
      'color': this.getTextOnPrimary(doseType)
    };
  }

  /**
   * Helper method to generate consistent hash for dose types
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Helper method to determine text color based on background brightness
   */
  private getContrastColor(backgroundColor: string): string {
    // For HSL colors, use a more sophisticated heuristic
    if (backgroundColor.includes('hsl')) {
      const lightnessMatch = backgroundColor.match(/,\s*(\d+)%,\s*(\d+)%/);
      if (lightnessMatch) {
        const lightness = parseInt(lightnessMatch[2]);
        // Use black text for light backgrounds, white for dark backgrounds
        return lightness > 60 ? '#000000' : '#FFFFFF';
      }
    }

    // For hex colors, calculate relative luminance
    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Calculate relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }

    // Default to white text for safety
    return '#FFFFFF';
  }
}

